import { Alert, Snackbar } from "@mui/material"
import { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { Controller, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { useCallback, useState } from "react"
import { useMutation } from "@tanstack/react-query"
// context
import useAuthenticationContext from "context/Authentication/useAuthenticationContext"
import { useModal } from "context/ModalContext/ModalContext"
// hooks
import { useLogin } from "./hooks/useLogin"
import { useToggleVisibility } from "hooks/useToggleVisibility"
import useToggleSnackbar from "hooks/useToggleSnackbar"
// components
import OnboardingLayout from "shared/components/OnboardingLayout"
import Button from "shared/components/Button"
import { TextField } from "shared/components/input"
// constants
import { ENTRY_POINT, FORGOT_PASSWORD } from "constants/routes"
import { colors } from "constants/colors"
// api
import { verifyEmail } from "api/auth"
// assets
import Onboarding2 from "assets/images/backgrounds/Onboarding2.svg"
// styles
import { SText, STitle } from "../styles"
import { useNavigate } from "react-router-dom"
import { MODALS } from "constants/modals"

interface FormData {
  email: string
  password: string
}

interface LoginProps {
  isModalView?: boolean
}

const validator = (t: TFunction) =>
  yup.object().shape({
    email: yup
      .string()
      .email(t("login:inputFields:email:errors:format"))
      .required(t("login:inputFields:email:errors:required")),
    password: yup.string().required(t("login:inputFields:password:errors:required")),
  })

const Login = ({ isModalView = false }: LoginProps) => {
  const { t } = useTranslation()
  const { logIn, isLoading, isError, error, submittedEmail } = useLogin()
  const { open, handleClose } = useToggleSnackbar(isError)
  const isEmailNotConfirmed = isError && !!error?.toLowerCase().includes("not confirmed")
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendError, setResendError] = useState(false)
  const { mutate: resendActivationEmail, isLoading: isResendLoading } = useMutation({
    mutationFn: () => verifyEmail({ email: submittedEmail }),
    onSuccess: () => setResendSuccess(true),
    onError: () => setResendError(true),
  })
  const { user } = useAuthenticationContext()
  const navigate = useNavigate()
  const { openModal } = useModal()
  const { isVisible: fieldFocused, handleShow } = useToggleVisibility()

  const { handleSubmit, formState, control } = useForm<FormData>({
    resolver: yupResolver(validator(t)),
    defaultValues: {
      email: user.email,
      password: "",
    },
    reValidateMode: "onChange",
  })

  const handleFormSubmit = ({ email, password }: FormData) => {
    logIn({ email, password })
  }

  const handleOpenForgotPassword = useCallback(() => {
    isModalView ? openModal(MODALS.AUTH.FORGOT_PASSWORD) : navigate(FORGOT_PASSWORD, { replace: true })
  }, [])

  return (
    <OnboardingLayout
      focused={fieldFocused}
      logoColor={colors.green.dark}
      logoNameColor={colors.green.dark}
      backBtnIconName="back"
      showBackBtn
      withoutFrame
      rightBackgroundSrc={Onboarding2}
      backPath={ENTRY_POINT}
      isModalView={isModalView}
      backModal={MODALS.AUTH.ENTRY_POINT}
    >
      <STitle>
        {t("login:title")}
        {" " + (user.userName ?? "")}
      </STitle>
      <SText variant="h5">{t("login:text")}</SText>

      <form
        style={{ height: "100%", flexGrow: 1, display: "flex", flexDirection: "column" }}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <Controller
          name="email"
          control={control}
          render={({
            field,
            formState: {
              errors: { email },
            },
          }) => (
            <TextField
              onFocus={handleShow}
              type="email"
              inputProps={field}
              error={!!email?.message}
              helperText={email?.message}
              sx={{ mb: 4 }}
              placeholder={t("login:inputFields:email:placeholder")}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({
            field,
            formState: {
              errors: { password },
            },
          }) => (
            <TextField
              onFocus={handleShow}
              onCopy={(e) => e.preventDefault()}
              type="password"
              inputProps={field}
              error={!!password?.message}
              helperText={password?.message}
              placeholder={t("login:inputFields:password:placeholder")}
            />
          )}
        />
        <Button
          sx={[
            ({
              palette,
              transitions: {
                duration: { standard },
                easing: { easeInOut },
              },
            }) => ({
              padding: "0 !important",
              ml: 2,
              mt: 2,
              mb: 8,
              transition: `all ${standard}ms ${easeInOut}`,
              color: palette.black.lacquer["400"],
              justifyContent: "flex-start",
              "&:hover": {
                color: palette.secondary.main,
                "& path, & rect": {
                  fill: palette.secondary.main,
                },
              },
            }),
          ]}
          color="secondary"
          fullWidth
          variant="text"
          onClick={handleOpenForgotPassword}
        >
          {t("login:forgotPasswordBtnText")}
        </Button>
        <Button
          sx={{ mt: "auto" }}
          type="submit"
          disabled={!!Object.keys(formState.errors).length || isLoading}
          color="secondary"
          fullWidth
          variant="contained"
        >
          {t("login:submitButtonText")}
        </Button>
      </form>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message={error}
        onClose={handleClose}
      >
        <Alert
          severity="error"
          sx={{ width: "100%", "& .MuiAlert-icon": { alignSelf: "center" } }}
          onClose={handleClose}
          closeText="Close"
        >
          {error}. {t("login:resendActivationText")}
          {isEmailNotConfirmed && (
            <Button
              variant="text"
              color="error"
              disabled={isResendLoading}
              size="small"
              sx={{
                p: "6px 7px !important",
                marginLeft: "4px",
                verticalAlign: "baseline",
                fontSize: "14px !important",
                "&:hover": {
                  background: "rgba(211,47,47,0.08)",
                },
                "&.Mui-disabled": {
                  color: "rgba(211,47,47,0.4)",
                },
              }}
              onClick={() => {
                resendActivationEmail()
                handleClose()
              }}
            >
              {t("login:resendActivationButton")}
            </Button>
          )}
        </Alert>
      </Snackbar>
      <Snackbar
        open={resendSuccess}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setResendSuccess(false)}
      >
        <Alert
          severity="success"
          sx={{ width: "100%", "& .MuiAlert-icon": { alignSelf: "center" } }}
          onClose={() => setResendSuccess(false)}
        >
          {t("login:resendActivationSuccess")}
        </Alert>
      </Snackbar>
      <Snackbar
        open={resendError}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setResendError(false)}
      >
        <Alert
          severity="error"
          sx={{ width: "100%", "& .MuiAlert-icon": { alignSelf: "center" } }}
          onClose={() => setResendError(false)}
        >
          {t("login:resendActivationError")}
        </Alert>
      </Snackbar>
    </OnboardingLayout>
  )
}

export default Login
